/** Vite dispatches this on `window` when a lazy chunk (or its CSS) fails to load. */
export const VITE_PRELOAD_ERROR_EVENT = 'vite:preloadError'

/** The build this tab last reloaded for a stale chunk, in sessionStorage. */
export const STALE_CHUNK_RELOAD_KEY = 'dope:stale-chunk-reloaded'

type StaleChunkReloadOptions = {
  target?: Window
  getStorage?: () => Storage
  getBuildId?: () => string
}

/**
 * The entry script URL (`/static/js/main.<hash>.js`) changes with every build
 * that changes the code.
 */
const getEntryScriptUrl = (): string =>
  document
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute('src') ?? ''

let reloadPending = false

/**
 * True once a stale-chunk reload has started in this page. Callers should keep
 * lazy imports pending until the reload replaces the page: a handled JS
 * failure resolves to `undefined`, and a handled CSS failure resolves to a
 * module without its styles.
 */
export const isStaleChunkReloadPending = (): boolean => reloadPending

/**
 * Records the reload attempt. False when this tab already reloaded for this
 * build, or storage fails.
 */
const claimReloadAttempt = (storage: Storage, buildId: string): boolean => {
  try {
    if (storage.getItem(STALE_CHUNK_RELOAD_KEY) === buildId) {
      return false
    }
    storage.setItem(STALE_CHUNK_RELOAD_KEY, buildId)
    return true
  } catch {
    return false
  }
}

/**
 * After a deploy, a tab still running the previous build can request a chunk
 * the new build removed. Reload once so the tab picks up the new index.html.
 * Each build gets one attempt per tab, so a tab open across several deploys
 * recovers from each, and a chunk still missing after the reload stops after
 * at most two reloads. A repeat failure, or storage that cannot record the
 * attempt, is left to the app's error handling instead of reloading in a loop.
 */
export const registerStaleChunkReload = ({
  target = window,
  getStorage = () => window.sessionStorage,
  getBuildId = getEntryScriptUrl,
}: StaleChunkReloadOptions = {}): void => {
  let storage: Storage
  try {
    // Reading window.sessionStorage throws when the browser blocks storage.
    storage = getStorage()
  } catch {
    return
  }
  target.addEventListener(VITE_PRELOAD_ERROR_EVENT, (event: Event) => {
    if (reloadPending) {
      // Another chunk failed while the reload is already on its way.
      event.preventDefault()
      return
    }
    if (!claimReloadAttempt(storage, getBuildId())) {
      return
    }
    reloadPending = true
    event.preventDefault()
    target.location.reload()
  })
}
